import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { InteractiveObjectType } from '../types';

export class InteractiveObject {
  public container: Container;
  public x: number;
  public y: number;
  public type: InteractiveObjectType;
  public name: string;
  public actionPrompt: string;
  public chaosPoints: number;

  public isDestroyed: boolean = false;
  public currentProgress: number = 0; // 0 to 100
  public requiredProgress: number = 100;
  public interactionRadius: number = 65;

  private visualGfx: Graphics;
  private destroyedGfx: Graphics;
  private promptContainer: Container;
  private progressBarGfx: Graphics;

  constructor(
    type: InteractiveObjectType,
    x: number,
    y: number,
    name: string,
    actionPrompt: string,
    chaosPoints: number
  ) {
    this.container = new Container();
    this.type = type;
    this.x = x;
    this.y = y;
    this.name = name;
    this.actionPrompt = actionPrompt;
    this.chaosPoints = chaosPoints;
    this.container.position.set(x, y);

    this.visualGfx = new Graphics();
    this.destroyedGfx = new Graphics();
    this.destroyedGfx.visible = false;

    this.container.addChild(this.visualGfx);
    this.container.addChild(this.destroyedGfx);

    this.promptContainer = new Container();
    this.promptContainer.position.set(0, -50);
    this.promptContainer.visible = false;

    this.progressBarGfx = new Graphics();
    this.promptContainer.addChild(this.progressBarGfx);

    const style = new TextStyle({
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      fontSize: 13,
      fontWeight: 'bold',
      fill: '#ffffff',
      stroke: { color: '#0f172a', width: 3 }
    });
    const label = new Text({ text: actionPrompt, style });
    label.anchor.set(0.5);
    label.position.set(0, -14);
    this.promptContainer.addChild(label);

    this.container.addChild(this.promptContainer);

    this.drawObject();
  }

  private drawObject() {
    switch (this.type) {
      case 'couch': {
        // Intact Sofa
        this.visualGfx.roundRect(-70, -35, 140, 70, 12);
        this.visualGfx.fill({ color: 0x2c3e50 });
        this.visualGfx.stroke({ width: 3, color: 0x34495e });
        this.visualGfx.roundRect(-60, -25, 55, 45, 8);
        this.visualGfx.fill({ color: 0x34495e });
        this.visualGfx.roundRect(5, -25, 55, 45, 8);
        this.visualGfx.fill({ color: 0x34495e });
        this.visualGfx.roundRect(-68, -32, 14, 64, 6);
        this.visualGfx.fill({ color: 0x1a252f });
        this.visualGfx.roundRect(54, -32, 14, 64, 6);
        this.visualGfx.fill({ color: 0x1a252f });

        // Destroyed Couch
        this.destroyedGfx.roundRect(-70, -35, 140, 70, 12);
        this.destroyedGfx.fill({ color: 0x2c3e50 });
        this.destroyedGfx.stroke({ width: 3, color: 0x34495e });
        this.destroyedGfx.circle(-20, 5, 12);
        this.destroyedGfx.circle(-10, 0, 10);
        this.destroyedGfx.circle(25, -10, 14);
        this.destroyedGfx.fill({ color: 0xf1f2f6 });
        this.destroyedGfx.moveTo(-45, -15);
        this.destroyedGfx.lineTo(-25, 15);
        this.destroyedGfx.moveTo(-40, -18);
        this.destroyedGfx.lineTo(-20, 12);
        this.destroyedGfx.moveTo(35, -20);
        this.destroyedGfx.lineTo(55, 10);
        this.destroyedGfx.stroke({ width: 3, color: 0xff4757 });
        break;
      }

      case 'curtains': {
        this.visualGfx.rect(-50, -12, 100, 24);
        this.visualGfx.fill({ color: 0x74b9ff, alpha: 0.5 });
        this.visualGfx.stroke({ width: 2, color: 0xffffff });
        this.visualGfx.rect(-58, -14, 116, 4);
        this.visualGfx.fill({ color: 0xdfe6e9 });
        this.visualGfx.roundRect(-54, -10, 30, 48, 6);
        this.visualGfx.fill({ color: 0xeb2f06 });
        this.visualGfx.roundRect(24, -10, 30, 48, 6);
        this.visualGfx.fill({ color: 0xeb2f06 });

        this.destroyedGfx.rect(-50, -12, 100, 24);
        this.destroyedGfx.fill({ color: 0x74b9ff, alpha: 0.5 });
        this.destroyedGfx.rect(-58, -14, 116, 4);
        this.destroyedGfx.fill({ color: 0xdfe6e9 });
        this.destroyedGfx.poly([
          { x: -54, y: -10 },
          { x: -24, y: -10 },
          { x: -30, y: 25 },
          { x: -36, y: 10 },
          { x: -44, y: 35 },
          { x: -54, y: 20 }
        ]);
        this.destroyedGfx.fill({ color: 0xeb2f06 });
        this.destroyedGfx.poly([
          { x: 24, y: -10 },
          { x: 54, y: -10 },
          { x: 50, y: 30 },
          { x: 42, y: 15 },
          { x: 34, y: 38 },
          { x: 24, y: 18 }
        ]);
        this.destroyedGfx.fill({ color: 0xeb2f06 });
        this.destroyedGfx.ellipse(-10, 30, 14, 6);
        this.destroyedGfx.fill({ color: 0xb71540 });
        break;
      }

      case 'vase': {
        this.visualGfx.circle(0, 0, 28);
        this.visualGfx.fill({ color: 0x805b38 });
        this.visualGfx.stroke({ width: 2, color: 0x543820 });
        this.visualGfx.circle(6, 6, 12);
        this.visualGfx.fill({ color: 0x00d2d3 });
        this.visualGfx.stroke({ width: 2, color: 0x01a3a4 });
        this.visualGfx.circle(7, 4, 3);
        this.visualGfx.fill({ color: 0xffffff });

        this.destroyedGfx.circle(0, 0, 28);
        this.destroyedGfx.fill({ color: 0x805b38 });
        this.destroyedGfx.poly([{ x: 20, y: 20 }, { x: 28, y: 14 }, { x: 26, y: 28 }]);
        this.destroyedGfx.fill({ color: 0x00d2d3 });
        this.destroyedGfx.poly([{ x: 12, y: 26 }, { x: 18, y: 34 }, { x: 8, y: 30 }]);
        this.destroyedGfx.fill({ color: 0x00d2d3 });
        this.destroyedGfx.ellipse(22, 22, 16, 10);
        this.destroyedGfx.fill({ color: 0x54a0ff, alpha: 0.6 });
        break;
      }

      case 'hairball_spot': {
        this.visualGfx.roundRect(-45, -30, 90, 60, 8);
        this.visualGfx.fill({ color: 0x6c5ce7 });
        this.visualGfx.stroke({ width: 3, color: 0xa29bfe });
        this.visualGfx.rect(-35, -20, 70, 40);
        this.visualGfx.stroke({ width: 1.5, color: 0xfdcb6e });

        this.destroyedGfx.roundRect(-45, -30, 90, 60, 8);
        this.destroyedGfx.fill({ color: 0x6c5ce7 });
        this.destroyedGfx.stroke({ width: 3, color: 0xa29bfe });
        this.destroyedGfx.rect(-35, -20, 70, 40);
        this.destroyedGfx.stroke({ width: 1.5, color: 0xfdcb6e });
        this.destroyedGfx.ellipse(0, 0, 16, 10);
        this.destroyedGfx.fill({ color: 0x2d3436, alpha: 0.3 });
        this.destroyedGfx.ellipse(0, -2, 10, 7);
        this.destroyedGfx.fill({ color: 0x6b4823 });
        break;
      }

      case 'toilet_paper': {
        this.visualGfx.roundRect(-16, -20, 32, 24, 6);
        this.visualGfx.fill({ color: 0xf1f2f6 });
        this.visualGfx.stroke({ width: 2, color: 0xdcdde1 });
        this.visualGfx.circle(0, -8, 5);
        this.visualGfx.fill({ color: 0x718093 });

        this.destroyedGfx.roundRect(-16, -20, 32, 24, 6);
        this.destroyedGfx.fill({ color: 0xdcdde1 });
        this.destroyedGfx.poly([
          { x: 0, y: 0 },
          { x: -20, y: 25 },
          { x: 15, y: 40 },
          { x: -35, y: 60 },
          { x: -10, y: 75 }
        ]);
        this.destroyedGfx.stroke({ width: 10, color: 0xf5f6fa, cap: 'round', join: 'round' });
        break;
      }

      case 'flower_pot': {
        this.visualGfx.circle(0, 0, 18);
        this.visualGfx.fill({ color: 0xd35400 });
        this.visualGfx.ellipse(-8, -12, 14, 7);
        this.visualGfx.fill({ color: 0x27ae60 });
        this.visualGfx.ellipse(10, -10, 15, 8);
        this.visualGfx.fill({ color: 0x2ecc71 });
        this.visualGfx.ellipse(0, 10, 16, 7);
        this.visualGfx.fill({ color: 0x1abc9c });

        this.destroyedGfx.ellipse(14, 10, 26, 16);
        this.destroyedGfx.fill({ color: 0x3d2314 });
        this.destroyedGfx.poly([{ x: -12, y: -10 }, { x: 6, y: -16 }, { x: 0, y: 8 }]);
        this.destroyedGfx.fill({ color: 0xd35400 });
        this.destroyedGfx.ellipse(20, 18, 12, 6);
        this.destroyedGfx.fill({ color: 0x27ae60 });
        break;
      }

      case 'laptop': {
        this.visualGfx.roundRect(-22, -16, 44, 32, 4);
        this.visualGfx.fill({ color: 0x2c3e50 });
        this.visualGfx.roundRect(-18, -13, 36, 24, 2);
        this.visualGfx.fill({ color: 0x3498db });

        this.destroyedGfx.roundRect(-22, -16, 44, 32, 4);
        this.destroyedGfx.fill({ color: 0x2c3e50 });
        this.destroyedGfx.roundRect(-18, -13, 36, 24, 2);
        this.destroyedGfx.fill({ color: 0x130f40 });
        this.destroyedGfx.moveTo(-10, -10);
        this.destroyedGfx.lineTo(12, 8);
        this.destroyedGfx.stroke({ width: 2, color: 0xffffff });
        this.destroyedGfx.ellipse(16, 12, 14, 8);
        this.destroyedGfx.fill({ color: 0x6f4e37 });
        break;
      }

      case 'closet': {
        // Wooden wardrobe
        this.visualGfx.roundRect(-40, -25, 80, 50, 6);
        this.visualGfx.fill({ color: 0x795548 });
        this.visualGfx.stroke({ width: 3, color: 0x4e342e });
        // Doors split
        this.visualGfx.moveTo(0, -25);
        this.visualGfx.lineTo(0, 25);
        this.visualGfx.stroke({ width: 2, color: 0x3e2723 });
        // Handles
        this.visualGfx.circle(-6, 0, 3);
        this.visualGfx.circle(6, 0, 3);
        this.visualGfx.fill({ color: 0xf1c40f });

        // Clothes thrown out everywhere
        this.destroyedGfx.roundRect(-40, -25, 80, 50, 6);
        this.destroyedGfx.fill({ color: 0x795548 });
        this.destroyedGfx.stroke({ width: 3, color: 0x4e342e });
        // Scattered socks and dresses on floor
        this.destroyedGfx.ellipse(-20, 35, 18, 8);
        this.destroyedGfx.fill({ color: 0xe74c3c });
        this.destroyedGfx.ellipse(25, 30, 16, 10);
        this.destroyedGfx.fill({ color: 0x9b59b6 });
        this.destroyedGfx.circle(0, 38, 7);
        this.destroyedGfx.fill({ color: 0x3498db });
        break;
      }

      case 'box': {
        // Amazon-style cardboard delivery box
        this.visualGfx.roundRect(-24, -20, 48, 40, 4);
        this.visualGfx.fill({ color: 0xcd853f });
        this.visualGfx.stroke({ width: 2, color: 0x8b4513 });
        // Tape
        this.visualGfx.rect(-6, -20, 12, 40);
        this.visualGfx.fill({ color: 0xdaa520, alpha: 0.6 });

        // Chewed up / shredded cardboard box
        this.destroyedGfx.roundRect(-24, -20, 48, 40, 4);
        this.destroyedGfx.fill({ color: 0xcd853f });
        this.destroyedGfx.stroke({ width: 2, color: 0x8b4513 });
        // Bite marks & torn flaps
        this.destroyedGfx.poly([{ x: -24, y: -20 }, { x: -10, y: -30 }, { x: 0, y: -20 }]);
        this.destroyedGfx.fill({ color: 0xcd853f });
        // Cardboard scraps
        this.destroyedGfx.rect(-32, 10, 6, 6);
        this.destroyedGfx.fill({ color: 0xcd853f });
        this.destroyedGfx.rect(28, 5, 8, 5);
        this.destroyedGfx.fill({ color: 0xcd853f });
        break;
      }

      case 'food_plate': {
        // Kitchen plate with fish/sausage
        this.visualGfx.circle(0, 0, 22);
        this.visualGfx.fill({ color: 0xffffff });
        this.visualGfx.stroke({ width: 2, color: 0xbdc3c7 });
        // Delicious sausage / fish steak
        this.visualGfx.ellipse(0, 0, 12, 6);
        this.visualGfx.fill({ color: 0xe74c3c });

        // Smeared greasy plate with stolen food
        this.destroyedGfx.circle(0, 0, 22);
        this.destroyedGfx.fill({ color: 0xffffff });
        this.destroyedGfx.stroke({ width: 2, color: 0xbdc3c7 });
        // Greasy smears on floor
        this.destroyedGfx.ellipse(15, 15, 14, 6);
        this.destroyedGfx.fill({ color: 0xe67e22, alpha: 0.5 });
        this.destroyedGfx.circle(28, 20, 4);
        this.destroyedGfx.fill({ color: 0xe74c3c }); // Dragged food piece
        break;
      }

      case 'sponge': {
        // Yellow kitchen dish sponge
        this.visualGfx.roundRect(-16, -10, 32, 20, 4);
        this.visualGfx.fill({ color: 0xf1c40f });
        this.visualGfx.roundRect(-16, 4, 32, 6, 2);
        this.visualGfx.fill({ color: 0x27ae60 }); // Green scrub side

        // Ripped sponge in 100 crumbs
        this.destroyedGfx.circle(-8, -4, 5);
        this.destroyedGfx.fill({ color: 0xf1c40f });
        this.destroyedGfx.circle(6, 6, 6);
        this.destroyedGfx.fill({ color: 0xf1c40f });
        this.destroyedGfx.circle(12, -8, 4);
        this.destroyedGfx.fill({ color: 0x27ae60 });
        this.destroyedGfx.circle(-14, 10, 4);
        this.destroyedGfx.fill({ color: 0x27ae60 });
        break;
      }

      case 'mirror': {
        // Hallway wall mirror
        this.visualGfx.roundRect(-20, -30, 40, 60, 4);
        this.visualGfx.fill({ color: 0x74b9ff, alpha: 0.7 });
        this.visualGfx.stroke({ width: 3, color: 0xdfe6e9 });

        // Paw smeared & cracked mirror
        this.destroyedGfx.roundRect(-20, -30, 40, 60, 4);
        this.destroyedGfx.fill({ color: 0x74b9ff, alpha: 0.7 });
        this.destroyedGfx.stroke({ width: 3, color: 0xdfe6e9 });
        // Nose/paw grease smudges
        this.destroyedGfx.circle(-4, -6, 6);
        this.destroyedGfx.fill({ color: 0xffffff, alpha: 0.4 });
        this.destroyedGfx.circle(6, 8, 5);
        this.destroyedGfx.fill({ color: 0xffffff, alpha: 0.4 });
        break;
      }
    }
  }

  public setPromptVisible(visible: boolean) {
    if (this.isDestroyed) {
      this.promptContainer.visible = false;
      return;
    }
    this.promptContainer.visible = visible;
  }

  public addProgress(amount: number): boolean {
    if (this.isDestroyed) return false;

    this.currentProgress += amount;
    this.updateProgressBar();

    if (this.currentProgress >= this.requiredProgress) {
      this.isDestroyed = true;
      this.visualGfx.visible = false;
      this.destroyedGfx.visible = true;
      this.promptContainer.visible = false;
      return true;
    }
    return false;
  }

  private updateProgressBar() {
    this.progressBarGfx.clear();
    const width = 60;
    const height = 8;
    this.progressBarGfx.roundRect(-width / 2, 4, width, height, 4);
    this.progressBarGfx.fill({ color: 0x1e272e, alpha: 0.8 });

    const fillW = Math.min(width, (this.currentProgress / this.requiredProgress) * width);
    this.progressBarGfx.roundRect(-width / 2, 4, fillW, height, 4);
    this.progressBarGfx.fill({ color: 0xff9f43 });
    this.progressBarGfx.stroke({ width: 1, color: 0xffffff });
  }
}
